import { ZIcon, ZModal, ZTypography } from "@atoms";
import { EColors } from "@atoms/ZColors/interface";
import { EFontWeights } from "@atoms/ZTypography/interface";
import { Divider } from "antd";
import { CascaderOptionType } from "antd/lib/cascader";
import React, { useContext, useEffect, useRef, useState } from "react";
import { VendorResource } from "@/Contracts/Vendor/Resources";
import { EIcons } from "@atoms/ZIcon/interface";
import { AuditLogsCategories } from "@/Contracts/Others/SalesActivity";
import {
	SyncError,
	ZomentumPSAErrorTypes,
	getZomentumEntityDisplayValue,
	ZomentumPSA
} from "@/Contracts";
import {
	featureFlagInterface,
	featureFlagProjectInterface,
	FlagsContext,
	flagsProviderDataInterface
} from "./FlagsContext";
import { getFeatureFlagProjectWithFlags } from "./Service/FeatureFlag";

const { ZText } = ZTypography;

const emptyFeatureFlagsList: Record<string, featureFlagInterface> = {};
const emptyFeatureFlagProjectDetails: featureFlagProjectInterface = {
	entity_lists: [],
	valid_environments_for_project: []
};

let cachedFeatureFlagsList: Record<string, featureFlagInterface> =
	emptyFeatureFlagsList;
let cachedFeatureFlagProjectDetails: featureFlagProjectInterface =
	emptyFeatureFlagProjectDetails;
let areFlagsCached = false;
let flagsRequestPromise: Promise<{
	featureFlagsList: Record<string, featureFlagInterface>;
	featureFlagsProjectData: featureFlagProjectInterface;
}> | null = null;
let resetToken = 0;
const resetListeners = new Set<() => void>();

export const resetFlagsCache = () => {
	cachedFeatureFlagsList = emptyFeatureFlagsList;
	cachedFeatureFlagProjectDetails = emptyFeatureFlagProjectDetails;
	areFlagsCached = false;
	flagsRequestPromise = null;
	resetToken++;
	resetListeners.forEach(listener => listener());
};

export * from "./common";
export * from "./tinyMCEPrePastePrompt";
export * from "./Documents";
export * from "./Documents/UI";
export * from "./Template";
export * from "./ProposalBuilder";
export * from "./MergeTags";
export * from "./Opportunity";
export * from "./Others/SalesActivity";
export * from "./Others/CsvParser";
export * from "./Others/Constraints";
export * from "./Service/GlobalSearch";
export * from "./Service/BackendService";
export * from "./Service/ChangeSuggestions";
export * from "./Service";
export * from "./Store";
export * from "./Currency";
export * from "./FlagsContext";
export * from "./superops/embedDetection";
export * from "./featureFlagOverride";

export const hubspotPipelineCascaderHeader = (
	<>
		<div className="border border-gray-10 mb-2">
			<ZText>PIPELINE</ZText>
		</div>
		<Divider className="-mx-4 w-49" />
	</>
);

export const hubspotStagesCascaderHeader = (
	<>
		<div className="border border-gray-10 mb-2">
			<ZText>STAGES</ZText>
		</div>
		<Divider className="-mx-4 w-49" />
	</>
);

export const HubspotFieldMappingHeadingNotes = (
	<ul className="mb-6 pl-6">
		<li>
			<ZText>
				Ensure the data type of the fields being mapped are same. Wrong
				data type field mapping may cause field not to be updated.
			</ZText>
		</li>
	</ul>
);

export const HubspotFieldMappingHeader = ({
	extraZomentumText,
	extraHubspotText
}: {
	extraZomentumText?: string;
	extraHubspotText?: string;
}) => (
	<div className="table-header table-view">
		<ZText className="bg-blue-1 mr-4" weight={EFontWeights.MEDIUM}>
			ZOMENTUM{extraZomentumText}
		</ZText>
		<ZText
			weight={EFontWeights.MEDIUM}
			color={EColors.GRAY}
			colorVariant={9}
		>
			HUBSPOT{extraHubspotText}
		</ZText>
	</div>
);

export const hubspotCascaderDisplayRender = (
	labels: string[],
	selectedOptions?: CascaderOptionType[]
) => {
	return labels.map((label, i, arr) => {
		const option = selectedOptions?.[i];
		return (
			<ZText key={option?.value}>
				{label} {!(i === arr.length - 1) && "|"}{" "}
			</ZText>
		);
	});
};

export const stopSharingResourceConfirmModal = (
	resource: VendorResource,
	stopSharingCallback: (resource: VendorResource) => void
) => {
	ZModal.confirm({
		title: (
			<ZText colorVariant={9} weight={EFontWeights.MEDIUM} level={1}>
				{`Are you sure you want to stop sharing ${resource.name}?`}
			</ZText>
		),
		content: (
			<ZText colorVariant={8} level={1}>
				{resource.is_folder
					? `Once you stop sharing this resource, all partners in this tier will lose access to this folder and resources inside this folder unless shared separately.`
					: `Once you stop sharing this resource, all partners in this tier will lose access to this resource.`}
			</ZText>
		),
		onOk: () => stopSharingCallback(resource),
		okText: "Yes, Stop Sharing",
		okType: "danger",
		icon: (
			<ZIcon
				name={EIcons.CIRCLEQUESTION}
				color={EColors.GOLD}
				colorVariant={6}
				className="anticon"
				size="md"
			/>
		),
		width: "600px",
		centered: true
	});
};

export const deleteResourceConfirmModal = (
	resource: VendorResource,
	deleteResourceCallback: (resourceId: string, resourceName?: string) => void
) => {
	ZModal.confirm({
		title: (
			<ZTypography.ZText
				colorVariant={9}
				weight={EFontWeights.MEDIUM}
				level={1}
			>
				{resource.is_folder
					? `Do you Want to delete this ${resource.name}`
					: `Do you Want to delete this resource?`}
			</ZTypography.ZText>
		),
		content: (
			<ZTypography.ZText colorVariant={8} level={1}>
				{resource.is_folder
					? `Once you delete this folder all the partners and tiers whom you have shared this resouce with will no longer be able to access the folder and resources inside this folder..`
					: `Once you delete this resource all the partners or tiers whom you have shared this resouce with will no longer be able to access it.`}
			</ZTypography.ZText>
		),
		onOk: () => deleteResourceCallback(resource.id, resource.name),
		okText: "Yes, Delete",
		okType: "danger",
		icon: (
			<ZIcon
				name={EIcons.CIRCLEQUESTION}
				color={EColors.GOLD}
				colorVariant={6}
				className="anticon"
				size="md"
			/>
		),
		width: "475px",
		centered: true
	});
};

export const getAuditLogSource = (
	syncSourceString: string | null
): AuditLogsCategories => {
	switch (syncSourceString) {
		case AuditLogsCategories.PreSalesSource:
		case ZomentumPSA.Hubspot:
		case ZomentumPSA.InfusionSoft: {
			return AuditLogsCategories.PreSalesSource;
		}
		case ZomentumPSA.Quickbooks: {
			return AuditLogsCategories.AccountingSource;
		}
		case AuditLogsCategories.CustomDistributorSource: {
			return AuditLogsCategories.CustomDistributorSource;
		}
		case AuditLogsCategories.PaymentsSource: {
			return AuditLogsCategories.PaymentsSource;
		}
		default: {
			return AuditLogsCategories.PSA;
		}
	}
};

export const getSerializedErrorMessage = (syncErrors: SyncError[]) => {
	return syncErrors.reduce<string>((prev, currentError) => {
		const errorType = currentError.sync_error_type;

		const additionalInfoLastIndex =
			currentError.sync_error_additional_info.length - 1;
		switch (errorType) {
			case ZomentumPSAErrorTypes.MatchingEntityExistsInIntegration:
				prev += currentError.sync_error_additional_info.reduce<string>(
					(prevString, currentErrorAdditionalInfo, currentIndex) => {
						prevString += `${getZomentumEntityDisplayValue(
							currentErrorAdditionalInfo.entity_type,
							true
						)} with same name exist ${
							currentIndex === additionalInfoLastIndex ? "" : ", "
						}`;
						return prevString;
					},
					""
				);
				break;
			case ZomentumPSAErrorTypes.DependanciesNotFound:
			case ZomentumPSAErrorTypes.InvalidObject:
				prev += currentError.sync_error_additional_info.reduce<string>(
					(prevString, currentErrorAdditionalInfo, currentIndex) => {
						prevString += `${currentErrorAdditionalInfo.errors?.join(
							", "
						)} ${
							currentIndex === additionalInfoLastIndex ? "" : ", "
						}`;
						return prevString;
					},
					""
				);
				break;
			case ZomentumPSAErrorTypes.PermissionMissing:
				prev += currentError.sync_error_additional_info.reduce<string>(
					(prevString, currentErrorAdditionalInfo, currentIndex) => {
						prevString += `${currentErrorAdditionalInfo.missing_permissions?.join(
							", "
						)} ${
							currentIndex === additionalInfoLastIndex ? "" : ", "
						}`;
						return prevString;
					},
					""
				);
				break;
			case ZomentumPSAErrorTypes.UnknownErrors:
				prev += currentError.sync_error_additional_info.reduce<string>(
					(prevString, currentErrorAdditionalInfo, currentIndex) => {
						prevString += `${
							currentErrorAdditionalInfo.description
						} ${
							currentIndex === additionalInfoLastIndex ? "" : ", "
						}`;
						return prevString;
					},
					""
				);
				break;
			case ZomentumPSAErrorTypes.EntityDeletedInIntegration:
				prev += "The entity does not exist";
				break;
			default:
				prev += "";
				break;
		}

		return prev;
	}, "");
};

export const FlagsProviderComponent: React.FC = ({ children }) => {
	const parentFlagsContext = useContext(FlagsContext);
	const [resetVersion, setResetVersion] = useState(0);
	const [featureFlagsList, setFeatureFlagsList] = useState<
		Record<string, featureFlagInterface>
	>(
		areFlagsCached
			? cachedFeatureFlagsList
			: parentFlagsContext.featureFlagsList
	);
	const [featureFlagsProjectDetails, setFeatureFlagsProjectDetails] =
		useState<featureFlagProjectInterface>(
			areFlagsCached
				? cachedFeatureFlagProjectDetails
				: parentFlagsContext.featureFlagsProjectData
		);
	const [flagsLoaded, setFlagsLoaded] = useState(
		areFlagsCached || parentFlagsContext.flagsLoaded
	);
	const hasSubscribedToFlagsRequest = useRef(false);

	useEffect(() => {
		const clearHandler = () => {
			setFeatureFlagsList(emptyFeatureFlagsList);
			setFeatureFlagsProjectDetails(emptyFeatureFlagProjectDetails);
			setFlagsLoaded(false);
			hasSubscribedToFlagsRequest.current = false;
			setResetVersion(version => version + 1);
		};
		resetListeners.add(clearHandler);
		return () => {
			resetListeners.delete(clearHandler);
		};
	}, []);

	const loadFlagsOnce = async () => {
		const tokenAtStart = resetToken;
		if (!flagsRequestPromise) {
			flagsRequestPromise = getFeatureFlagProjectWithFlags()
				.then(projectWithFlags => {
					if (resetToken !== tokenAtStart) {
						return {
							featureFlagsList: emptyFeatureFlagsList,
							featureFlagsProjectData:
								emptyFeatureFlagProjectDetails
						};
					}

					const featureFlagsMapping: Record<
						string,
						featureFlagInterface
					> = {};
					projectWithFlags?.flags?.forEach(
						(flag: featureFlagInterface) => {
							featureFlagsMapping[flag.name] = flag;
						}
					);

					const nextFeatureFlagsProjectDetails = {
						entity_lists: projectWithFlags?.entity_lists ?? [],
						valid_environments_for_project:
							projectWithFlags?.valid_environments_for_project ??
							[]
					};

					cachedFeatureFlagsList = featureFlagsMapping;
					cachedFeatureFlagProjectDetails =
						nextFeatureFlagsProjectDetails;
					areFlagsCached = true;

					return {
						featureFlagsList: featureFlagsMapping,
						featureFlagsProjectData: nextFeatureFlagsProjectDetails
					};
				})
				.catch(err => {
					console.error(err);
					flagsRequestPromise = null;
					if (resetToken === tokenAtStart) {
						hasSubscribedToFlagsRequest.current = false;
					}
					throw err;
				});
		}

		return flagsRequestPromise;
	};

	useEffect(() => {
		if (areFlagsCached) {
			setFeatureFlagsList(cachedFeatureFlagsList);
			setFeatureFlagsProjectDetails(cachedFeatureFlagProjectDetails);
			setFlagsLoaded(true);
			return;
		}

		if (parentFlagsContext.flagsLoaded) {
			setFeatureFlagsList(parentFlagsContext.featureFlagsList);
			setFeatureFlagsProjectDetails(
				parentFlagsContext.featureFlagsProjectData
			);
			setFlagsLoaded(true);
			return;
		}

		if (hasSubscribedToFlagsRequest.current) {
			return;
		}

		hasSubscribedToFlagsRequest.current = true;
		const tokenAtStart = resetToken;

		loadFlagsOnce()
			.then(response => {
				if (resetToken !== tokenAtStart) return;
				setFeatureFlagsList(response.featureFlagsList);
				setFeatureFlagsProjectDetails(response.featureFlagsProjectData);
				setFlagsLoaded(true);
			})
			.catch(() => {
				hasSubscribedToFlagsRequest.current = false;
			});
	}, [parentFlagsContext.flagsLoaded, resetVersion]);

	const finalValue: flagsProviderDataInterface = {
		featureFlagsList: featureFlagsList,
		featureFlagsProjectData: featureFlagsProjectDetails,
		flagsLoaded
	};

	return (
		<FlagsContext.Provider value={finalValue}>
			{children}
		</FlagsContext.Provider>
	);
};
